import Container, { Service } from 'typedi';
import { LoggerProvider } from '../logger';
import {
    SessionStorageToken,
    UserContextToken,
    FeatureAvailabilityToken,
    WindowToken,
} from '../../injection-tokens';
import { CRMService } from '../crm/crm.service';
import { IUserContext } from '../user-context/user-context-interface';
import { Utils } from '../utils';
import { WebLoginService } from '../web-login/web.login.service';
import {
    ActionID,
    AppIdentifiers,
    AreaType,
    IOpenCISResultData,
    IPerformActionTopicPayload,
    MessageBroker,
    TriggerType,
    CrossSellChannel,
    ILogger,
    OfferingNames,
} from '@sparkware/uc-sdk-core';
import { CookieStoreItemService } from '../storage/implementations';
import { UrlUtils } from '../utils/urlUtils';
import { IFeatureAvailability } from '../feature/feature-availability/feature-availability-interface';
import { ABTestFeatures, Offering } from '../action-handler/enums';
import { Brand, Features } from '../../models/enums/general-enums';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
} from '@888unifiedclient/event-formatter';
import { ClickStreamTrackingProvider } from '../tracking';
import { IActionHandlerFactory } from '../action-handler/models/IActionHandlerFactory';
import { BrandUtils } from '../../../Modules/Utils/BrandUtils';
import { INativeService } from '../native/models/INativeService';
import { LoaderManager } from '../../loaders/LoaderManager';
import { ClientProductService } from '../gnwp/client-product-service';
import { add } from 'lodash';

interface OpenCasinoParams {
    productOfferingId: number;
    subBrandId: number;
    productPackageId: number;
    isRealMode: number;
    specificNavigation?: string;
    target: string;
    linkId?: string;
    checkIfTargetStillExists?: boolean;
    addToQueryString?: any;
}

@Service()
export class CrossSellService {
    private readonly _logger: ILogger;
    private readonly _window: Window;
    private readonly _clientProductService: ClientProductService;
    private readonly _channel: CrossSellChannel;
    private readonly _clickStreamTrackingProvider: ClickStreamTrackingProvider;
    private readonly _cookieStoreItemService: CookieStoreItemService;
    private readonly _crmService: CRMService;
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _featureAvailability: IFeatureAvailability;
    private readonly _sessionStorage: Storage;
    private readonly _urlUtils: UrlUtils;
    private readonly _userContext: IUserContext;
    private readonly _utils: Utils;
    private readonly _webLoginService: WebLoginService;

    private get _nativeServicePromise(): Promise<INativeService> {
        return LoaderManager.Instance.NativeServiceLoader.Instance;
    }

    private get _actionHandlerFactoryPromise(): Promise<IActionHandlerFactory> {
        return LoaderManager.Instance.ActionHandlerFactoryLoader.Instance;
    }

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('CrossSellService');
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('CrossSellService');
        this._channel = MessageBroker.getInstance().crossSell;
        this._userContext = Container.get(UserContextToken);
        this._crmService = Container.get(CRMService);
        this._utils = Container.get(Utils);
        this._urlUtils = Container.get(UrlUtils);
        this._sessionStorage = Container.get(SessionStorageToken);
        this._clientProductService = Container.get(ClientProductService);
        this._cookieStoreItemService = Container.get(CookieStoreItemService);
        this._featureAvailability = Container.get(FeatureAvailabilityToken);
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._webLoginService = Container.get(WebLoginService);
    }

    public OpenSport = (data: any) => {
        if (this._userContext.IsAuthenticated) {
            this.SportAutoLogin();
        }
    };

    public SportAutoLogin() {
        this._clientProductService.openClientAuthenticated({
            productOfferingId: 4,
            subBrandId: null,
            productPackageId: 9,
            isRealMode: true,
            additionalActivationParameters: null,
        });
    }

    public OpenCasino = (data: any) => {
        if (
            this._window.pageContextManager.getUserData().hasLoginPrivileges &&
            !this._window.pageContextManager
                .getUserData()
                .allowedOfferings?.includes(OfferingNames.Casino.toLocaleLowerCase())
        ) {
            return;
        }

        const deepLinkData = data?.deeplink || null;

        if (this._userContext.IsAuthenticated) {
            const linkId = data?.linkId || null;

            this.CasinoAutoLogin(data?.target || null, deepLinkData, linkId);
        } else this.OpenAnonymousCasino(data);
    };

    public OpenCIS = async (data) => {
        let isCISEnabled = false;

        const nativeService = await this._nativeServicePromise;
        if (nativeService) {
            const isNativeCISEnabled = nativeService.findIfCISIsEnabled();
            if (isNativeCISEnabled)
                isCISEnabled = this._featureAvailability.IsFeatureEnabled(
                    ABTestFeatures.CASINO_IN_SPORT,
                );
        }
        if (!isCISEnabled) return false;

        const action: IPerformActionTopicPayload = {
            actionID: ActionID.openCIS,
            correlationID: this._utils.generateCorrelationID(),
            launchInfo: {
                businessCorrelationID: this._utils.generateCorrelationID(),
                sequentialCorrelationID: 1,
                channel: {
                    area: AreaType.button,
                    source: 'Unified Client',
                    element: 'Default',
                },
                trigger: TriggerType.userSelection,
                sourceAppID: AppIdentifiers.UnifiedClient,
                sourceAppVersion: '0.0.0',
                clientVersion: this._window.pageContextManager.getDeviceData().clientVersion,
            },
            actionData: data,
        };

        const actionHandlerFactory = await this._actionHandlerFactoryPromise;
        await actionHandlerFactory.performAction(action);

        return true;
    };

    public OpenAnonymousCasino = (data) => {
        const isIOSNativeOpenCasinoFeature = this._findIfIsIOSNativeOpenCasinoFeature();
        const { brandId } = this._window.pageContextManager.getBrandData();
        if (
            isIOSNativeOpenCasinoFeature &&
            (brandId === Brand.Sport_Com || brandId === Brand.Sport_UK)
        ) {
            //Feature to open casino inside sport - NOT USED at this moment
            this._sessionStorage.setItem('IsCasinoAutoLogin', 'true');

            void this._webLoginService.Show();
        } else {
            const queryString = data?.deeplink ? this._getOpenGameQS(data?.deeplink, true) : '';

            const casinoUrl = !isIOSNativeOpenCasinoFeature
                ? this._getCasinoUrl()
                : this._window.pageContextManager.getUrlResourceData().iosNativeCasinoUrl;

            if (casinoUrl) {
                const mrgBrands = [Brand.Sport_MG_SE, Brand.Sport_MG_DK, Brand.Sport_MG_MGA];
                const openCasinoUrl = mrgBrands.some((b) => b === brandId)
                    ? casinoUrl + queryString
                    : this._urlUtils.getURLWithSCut(casinoUrl + queryString);

                if (data?.target === 'open-cis') {
                    const relativeUrl = openCasinoUrl.replace(casinoUrl, '');
                    const parsedUrl = this._urlUtils.getParsedUrl(relativeUrl);
                    const queryObject = Object.fromEntries(parsedUrl.searchParams.entries());
                    const cisResultData: IOpenCISResultData = {
                        relativeUrl,
                        url: openCasinoUrl,
                        queryObject,
                    };
                    this._channel.topics.CTA_CasinoClient_GetURL.publish(
                        { publisher: 'UC-CrossSellService' },
                        cisResultData,
                    );
                } else {
                    this._crmService.OpenExternalUrl(openCasinoUrl, data?.target);
                }
            }
        }
    };

    public CasinoAutoLogin = (formTarget?, deepLinkData?, casinoLinkId?) => {
        const params: OpenCasinoParams = this._getCasinoAutoLoginData(
            formTarget,
            deepLinkData,
            casinoLinkId,
        );

        const {
            isRealMode,
            productOfferingId,
            productPackageId,
            subBrandId,
            target,
            addToQueryString,
            checkIfTargetStillExists,
            linkId,
            specificNavigation,
        } = params;

        const eventParams = Object.keys(params).reduce((acc, nextKey) => {
            acc[`ccs_${nextKey}`] = params[nextKey];
            return acc;
        }, {});
        const formatter = this._eventFormatterBuilder.createFormatter('casinoAutoLogin');
        const elasticEventObj = formatter.formatUCEvent(
            { message: 'Authenticated cross to Casino' },
            { correlationID: this._utils.generateCorrelationID() },
            {
                event: 'casino-cross-sell',
                ...eventParams,
            },
        );
        this._clickStreamTrackingProvider.sendEventV2(elasticEventObj);

        void this._clientProductService.openClientAuthenticated({
            productOfferingId,
            subBrandId,
            productPackageId,
            isRealMode: isRealMode === 1,
            additionalActivationParameters: specificNavigation,
            target,
            linkId,
            checkIfTargetStillExists,
            addToQueryString,
        });
    };

    public AutoLoginToLiveDealer(target?) {
        let deviceInformation = this._window.pageContextManager.getDeviceData();

        target = target || '_blank';
        let subBrandId = null; // todo: change to 78 for scasino

        target = target || 'Casino';
        if (deviceInformation.isMobile) {
            this.getCasinoForMobileParams(target, 3, subBrandId, 88, 1, 1);
        } else {
            this._clientProductService.openClientAuthenticated({
                productOfferingId: 3,
                subBrandId,
                productPackageId: 88,
                isRealMode: true,
                additionalActivationParameters: '',
                target,
                linkId: '1',
            });
        }
    }

    public GamesAutoLogin() {
        let deviceInformation = this._window.pageContextManager.getDeviceData();
        if (deviceInformation.isMobile) {
            this.OpenGamesForMobile();
        } else {
            this._clientProductService.openClientAuthenticated({
                productOfferingId: 3,
                subBrandId: 79,
                productPackageId: 88,
                isRealMode: true,
                additionalActivationParameters: '',
                target: '',
                linkId: '30',
            });
        }
    }

    public async PokerAutoLogin(target = '_blank') {
        const specificNavigation =
            '<specificNavigation><CallBackURL>' +
            this._window.location.origin +
            '</CallBackURL></specificNavigation>';
        await this._clientProductService.openClientAuthenticated({
            productOfferingId: 2,
            subBrandId: null,
            productPackageId: 146,
            isRealMode: true,
            additionalActivationParameters: specificNavigation,
            target,
        });
    }

    public async PISAutoLogin(target?) {
        const isNative = this._utils.findIfIsNative();
        target = target || '_blank';
        if (isNative) {
            await this._openPISForNative();
        } else {
            this._openPISForPC(target);
        }
    }

    OpenCGPForMobile(
        target,
        productOfferingId,
        subBrandId,
        productPackageId,
        isRealMode,
        linkId,
        deepLinkData,
    ) {
        let addToQueryString = null;
        if (deepLinkData) {
            addToQueryString = { deeplink: deepLinkData };
        }

        const { schemaUrl } = this._window.pageContextManager.getNativeData();

        if (this._utils.findIfIsNative()) {
            let specificNavigation =
                '<specificNavigation><CallBackURL>' +
                schemaUrl +
                '</CallBackURL></specificNavigation>';
            this._clientProductService.openClientAuthenticated({
                productOfferingId,
                subBrandId,
                productPackageId,
                isRealMode,
                additionalActivationParameters: specificNavigation,
                target: 'native',
                linkId,
                addToQueryString,
            });
        } else {
            let specificNavigation =
                '<specificNavigation><CallBackURL>' +
                this._window.location.origin +
                '</CallBackURL></specificNavigation>';
            target = target || '_self';
            this._clientProductService.openClientAuthenticated({
                productOfferingId,
                subBrandId,
                productPackageId,
                isRealMode,
                additionalActivationParameters: specificNavigation,
                target,
                linkId,
                addToQueryString,
            });
        }
    }

    getCasinoForMobileParams = (
        target,
        productOfferingId?,
        subBrandId?,
        productPackageId?,
        isRealMode?,
        linkId?,
        addToQueryString?,
    ): OpenCasinoParams => {
        const params: OpenCasinoParams = {
            target,
            productOfferingId,
            subBrandId,
            isRealMode,
            linkId,
            addToQueryString,
            checkIfTargetStillExists: undefined,
            productPackageId,
        };

        const { schemaUrl } = this._window.pageContextManager.getNativeData();

        if (this._utils.findIfIsNative()) {
            params.specificNavigation = `<specificNavigation>
                    <CallBackURL>${schemaUrl}</CallBackURL>
                </specificNavigation>`;

            params.target = ['casinoNativeIOS', 'open-cis'].some(
                (targetTest) => target === targetTest,
            )
                ? target
                : 'native';
        } else {
            params.specificNavigation = `<specificNavigation><CallBackURL>${this._window.location.origin}</CallBackURL></specificNavigation>`;
            params.target = target || '_self';
        }

        return params;
    };

    public OpenGamesForMobile() {
        if (this._utils.findIfIsNative()) {
            const { schemaUrl } = this._window.pageContextManager.getNativeData();
            const specificNavigation =
                '<specificNavigation><CallBackURL>' +
                schemaUrl +
                '</CallBackURL></specificNavigation>';
            this._clientProductService.openClientAuthenticated({
                productOfferingId: 3,
                subBrandId: 79,
                productPackageId: 88,
                isRealMode: true,
                additionalActivationParameters: specificNavigation,
                target: 'native',
                linkId: '30',
            });
        } else {
            const specificNavigation =
                '<specificNavigation><CallBackURL>' +
                this._window.location.origin +
                '</CallBackURL></specificNavigation>';

            this._clientProductService.openClientAuthenticated({
                productOfferingId: 3,
                subBrandId: 79,
                productPackageId: 88,
                isRealMode: true,
                additionalActivationParameters: specificNavigation,
                target: '',
                linkId: '30',
            });
        }
    }

    public VirtualSportAutoLogin() {
        this._getCasinoAutoLoginData('_blank', undefined, true);
    }

    private _getCasinoAutoLoginData(target?, deepLinkData?, linkId?): OpenCasinoParams {
        const isIOSNativeOpenCasinoFeature = this._findIfIsIOSNativeOpenCasinoFeature();
        const virtualSportRedirect = linkId ? linkId : undefined;
        const deviceInformation = this._window.pageContextManager.getDeviceData();
        let params: OpenCasinoParams = {} as OpenCasinoParams;
        const currentDomain = this._urlUtils.getDomain();
        const casinoDomain = this._urlUtils.getDomain(this._getCasinoUrl());
        const brandId = this._window.pageContextManager.getBrandData().brandId;
        target =
            isIOSNativeOpenCasinoFeature && target !== 'open-cis'
                ? 'casinoNativeIOS'
                : target ||
                  (currentDomain === casinoDomain &&
                  (brandId === Brand.Sport_MG_SE || brandId === Brand.Sport_MG_DK)
                      ? '_self'
                      : '_blank');
        let subBrandId = null; // todo: change to 78 for scasino

        let addToQueryString = null;
        if (deepLinkData) {
            addToQueryString = { deeplink: deepLinkData };
        }
        const userSelectedState = this._cookieStoreItemService.get('userSelectedState');
        if (userSelectedState) {
            if (addToQueryString) {
                addToQueryString.userSelectedState = userSelectedState;
            } else {
                addToQueryString = { userSelectedState };
            }
        }
        if (deviceInformation.isMobile || deviceInformation.isHybrid) {
            params = this.getCasinoForMobileParams(
                target,
                3,
                subBrandId,
                88,
                1,
                virtualSportRedirect ? virtualSportRedirect : '',
                addToQueryString,
            );
        } else {
            params = {
                productOfferingId: 3,
                subBrandId,
                productPackageId: 88,
                isRealMode: 1,
                specificNavigation: null,
                target,
                linkId: virtualSportRedirect,
                checkIfTargetStillExists: undefined,
                addToQueryString,
            };
        }

        return params;
    }

    private _openPISForPC(target) {
        if (this._userContext.IsAuthenticated) {
            this._clientProductService.openClientAuthenticated({
                productOfferingId: null,
                subBrandId: 80,
                productPackageId: 146,
                isRealMode: true,
                additionalActivationParameters: null,
                target,
                linkId: null,
            });
        } else {
            const openPokerUrl = this._urlUtils.getURLWithSCut('https://www.888poker.com/');
            this._crmService.OpenExternalUrl(openPokerUrl);
        }
    }

    private async _openPISForNative() {
        const nativeService = await this._nativeServicePromise;
        if (nativeService) {
            nativeService.openExternalLink(this._getPISSmartLink());
        }
    }

    private _findIfIsIOSNativeOpenCasinoFeature = (): boolean => {
        const isNative = this._utils.findIfIsNative();
        const isIOSNativeCasinoActive = this._featureAvailability.IsFeatureEnabled(
            Features.IOS_NATIVE_CASINO,
        );

        if (isNative) {
            return (
                !!this._window.pageContextManager.getDeviceData().isIOS && !!isIOSNativeCasinoActive
            );
        }

        return false;
    };

    private _getOpenGameQS = (deepLinkData, isQSEmpty) => {
        let result = '';

        result = `#${encodeURIComponent(`deeplink=${JSON.stringify(deepLinkData)}`)}`;

        return result;
    };

    private _getCasinoUrl = () => {
        const { brandId } = this._window.pageContextManager.getBrandData();

        let casinoUrl = null;

        switch (brandId) {
            case Brand.Sport_Spoker:
                if (BrandUtils.isPokerClient()) casinoUrl = `https://www.888casino.com/`;
                break;
            case Brand.Sport_Scasino:
            case Brand.Sport_UK:
            case Brand.Sport_Com:
                casinoUrl = 'https://www.888casino.com/';
                break;
            case Brand.Sport_IT:
                casinoUrl = 'https://www.888casino.it/';
                break;
            case Brand.Sport_ES:
                casinoUrl = 'https://www.888casino.es/';
                break;
            case Brand.Sport_DK:
                casinoUrl = 'https://www.888casino.dk/';
                break;
            case Brand.Sport_RO: {
                casinoUrl = 'https://www.888casino.ro/';
                break;
            }
            case Brand.Sport_SE:
                casinoUrl = 'https://www.888casino.se/';
                break;
            case Brand.Sport_CA_ON:
            case Brand.Sport_CA_AB:
                casinoUrl = 'https://www.888casino.ca/';
                break;
            case Brand.Sport_NL:
                casinoUrl = 'https://www.888casino.nl/';
                break;
            case Brand.Sport_US_MI:
                casinoUrl = 'https://casino.sisportsbook.com/';
                break;
            case Brand.Sport_MG_SE:
            case Brand.Sport_MG_DK:
            case Brand.Sport_MG_MGA:
                casinoUrl = '/';
                break;
        }

        return casinoUrl;
    };

    private _getPISSmartLink = (): string => {
        let smartLinkURL: string;
        const userCountry = this._window.pageContextManager.getGlobalizationData().country;
        const { isIOS } = this._window.pageContextManager.getDeviceData();
        const isUKorIRL = ['IRL', 'GBR'].some(
            (country) => country.toLowerCase() === userCountry?.toLowerCase(),
        );
        const { crossSells } = this._window.pageContextManager.getSiteData();
        const pisCrossSellData = crossSells?.filter((crossSellData) => crossSellData.id === 'PIS');
        if (pisCrossSellData && pisCrossSellData.length > 0) {
            if (isIOS) smartLinkURL = pisCrossSellData[0].nativeSmartLink;
            else
                smartLinkURL = isUKorIRL
                    ? pisCrossSellData[0].nativeSmartLink + 'GP'
                    : pisCrossSellData[0].nativeSmartLinkSecondary;
        }
        return smartLinkURL;
    };
}
